/**
 * Deterministic lockstep sync for P2P battles.
 * Both players submit actions each turn; the engine resolves them with the same seed.
 */

import type { P2PMessage, BattleAction, BattleConfig, TrainerProfile, PokemonSet, BattleState } from "../engine/types";
import type { IP2PConnection } from "./base";
import { buildHello, buildTeam, isVersionCompatible } from "./protocol";
import { APP_VERSION } from "../config/constants";
import { generateSeed } from "../engine/battle";

/** Sync session state. */
export type SyncState = {
  phase: "waiting" | "hello" | "team_exchange" | "config" | "ready" | "battle" | "ended";
  isHost: boolean;
  myProfile: TrainerProfile | null;
  peerProfile: TrainerProfile | null;
  myTeamHash: string | null;
  peerTeamHash: string | null;
  config: BattleConfig | null;
  pendingAction: BattleAction | null;
  turn: number;
  peerConnected: boolean;
  lastPongTime: number;
  myReady: boolean;
  peerReady: boolean;
  myReadyTeam: PokemonSet[] | null;
  peerReadyTeam: PokemonSet[] | null;
};

/** Events emitted by the sync manager. */
export type SyncEvents = {
  onStateChange: (state: SyncState) => void;
  onPeerHello: (profile: TrainerProfile, version: string) => void;
  onPeerTeamHash: (hash: string) => void;
  onConfigReceived: (config: BattleConfig) => void;
  onBattleStart: (seed: number, config: BattleConfig, hostTeam: PokemonSet[], hostProfile: TrainerProfile) => void;
  onGuestTeam: (team: PokemonSet[], profile: TrainerProfile) => void;
  onPeerReady: (profile: TrainerProfile) => void;
  onBothReady: (seed: number, config: BattleConfig, hostTeam: PokemonSet[], hostProfile: TrainerProfile, guestTeam: PokemonSet[], guestProfile: TrainerProfile) => void;
  onActionReceived: (turn: number, action: BattleAction) => void;
  onTurnReady: (turn: number, myAction: BattleAction, peerAction: BattleAction) => void;
  onPeerDisconnect: () => void;
  onPeerReconnect: () => void;
  onChat: (text: string) => void;
  onBattleState?: (state: BattleState, profiles: { p1: TrainerProfile; p2: TrainerProfile }) => void;
  onError: (msg: string) => void;
};

/** Manages the deterministic lockstep protocol over a P2P connection. */
export class SyncManager {
  private conn: IP2PConnection;
  private events: SyncEvents;
  private state: SyncState;
  private peerActionForTurn: Map<number, BattleAction> = new Map();
  private turnReadyCallback: ((turn: number, myAction: BattleAction, peerAction: BattleAction) => void) | null = null;
  private chatCallback: ((text: string) => void) | null = null;
  private rematchRequestedCallback: ((config: BattleConfig, seed: number) => void) | null = null;
  private rematchAcceptedCallback: ((config: BattleConfig, seed: number) => void) | null = null;
  private rematchDeclinedCallback: (() => void) | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(conn: IP2PConnection, isHost: boolean, events: SyncEvents) {
    this.conn = conn;
    this.events = events;
    this.state = {
      phase: "waiting",
      isHost,
      myProfile: null,
      peerProfile: null,
      myTeamHash: null,
      peerTeamHash: null,
      config: null,
      pendingAction: null,
      turn: 0,
      peerConnected: true,
      lastPongTime: Date.now(),
      myReady: false,
      peerReady: false,
      myReadyTeam: null,
      peerReadyTeam: null,
    };
  }

  getState(): SyncState {
    return this.state;
  }

  /** Handle an incoming P2P message. */
  handleMessage(msg: P2PMessage): void {
    switch (msg.type) {
      case "HELLO":
        if (!isVersionCompatible(msg.clientVersion)) {
          this.events.onError(`Version mismatch: opponent is ${msg.clientVersion}, expected ${APP_VERSION}`);
          return;
        }
        this.state.peerProfile = msg.profile;
        this.state.phase = "hello";
        this.events.onPeerHello(msg.profile, msg.clientVersion);
        this.updateState();
        break;
      case "TEAM":
        this.state.peerTeamHash = msg.teamHash;
        this.events.onPeerTeamHash(msg.teamHash);
        this.updateState();
        break;
      case "BATTLE_CONFIG":
        this.state.config = msg.config;
        this.events.onConfigReceived(msg.config);
        this.updateState();
        break;
      case "READY":
        this.state.peerReady = true;
        this.state.peerReadyTeam = msg.team;
        if (this.state.peerProfile && this.state.peerProfile.name !== msg.profile.name) {
          this.state.peerProfile = msg.profile;
        }
        this.events.onPeerReady(msg.profile);
        this.updateState();
        if (this.state.myReady && this.state.isHost) {
          this.startBattleFromReadyState();
        }
        break;
      case "BATTLE_START":
        this.state.config = msg.config;
        this.state.phase = "battle";
        this.events.onBattleStart(msg.seed, msg.config, msg.hostTeam, msg.hostProfile);
        this.updateState();
        break;
      case "GUEST_TEAM":
        this.events.onGuestTeam(msg.team, msg.profile);
        break;
      case "ACTION":
        this.peerActionForTurn.set(msg.turn, msg.action);
        this.events.onActionReceived(msg.turn, msg.action);
        if (this.state.pendingAction && msg.turn === this.state.turn) {
          this.events.onTurnReady(msg.turn, this.state.pendingAction, msg.action);
          this.turnReadyCallback?.(msg.turn, this.state.pendingAction, msg.action);
        }
        break;
      case "REMATCH_REQUEST":
        this.rematchRequestedCallback?.(msg.config, msg.seed);
        break;
      case "REMATCH_ACCEPT":
        this.rematchAcceptedCallback?.(msg.config, msg.seed);
        break;
      case "REMATCH_DECLINE":
        this.rematchDeclinedCallback?.();
        break;
      case "SYNC_STATE":
        this.applySyncState(msg.turn, msg.config, msg.pendingAction);
        break;
      case "BATTLE_STATE":
        this.events.onBattleState?.(msg.state, msg.profiles);
        break;
      case "PING":
        void this.conn.send({ type: "PONG", timestamp: Date.now() });
        break;
      case "PONG":
        this.handlePong();
        break;
      case "CHAT":
        this.events.onChat(msg.text);
        this.chatCallback?.(msg.text);
        break;
      default:
        break;
    }
  }

  /** Send HELLO with our trainer profile. */
  async sendHello(profile: TrainerProfile): Promise<void> {
    this.state.myProfile = profile;
    await this.conn.send(buildHello(profile));
  }

  /** Send team hash (not full team data — that's post-battle only). */
  async sendTeamHash(teamMembers: unknown[]): Promise<void> {
    const msg = await buildTeam(teamMembers);
    this.state.myTeamHash = (msg as { type: "TEAM"; teamHash: string }).teamHash;
    await this.conn.send(msg);
  }

  /** Host: send battle config. */
  async sendConfig(config: BattleConfig): Promise<void> {
    this.state.config = config;
    await this.conn.send({ type: "BATTLE_CONFIG", config });
  }

  /** Send ready signal with our full team/profile. */
  async sendReady(team: PokemonSet[], profile: TrainerProfile): Promise<void> {
    this.state.myReady = true;
    this.state.myReadyTeam = team;
    if (!this.state.myProfile) {
      this.state.myProfile = profile;
    }
    await this.conn.send({ type: "READY", team, profile });
    this.updateState();
    if (this.state.peerReady && this.state.isHost) {
      this.startBattleFromReadyState();
    }
  }

  /** Send battle start signal (host only). */
  async sendBattleStart(seed: number, config: BattleConfig, hostTeam: PokemonSet[], hostProfile: TrainerProfile): Promise<void> {
    await this.conn.send({ type: "BATTLE_START", seed, config, hostTeam, hostProfile });
    this.updateState();
  }

  /** Send guest team data in response to BATTLE_START. */
  async sendGuestTeam(team: PokemonSet[], profile: TrainerProfile): Promise<void> {
    await this.conn.send({ type: "GUEST_TEAM", team, profile });
  }

  /** Host: once both sides are ready, start the battle with a shared seed. */
  private startBattleFromReadyState(): void {
    if (!this.state.isHost || !this.state.myReady || !this.state.peerReady) return;
    if (!this.state.myReadyTeam || !this.state.peerReadyTeam || !this.state.config || !this.state.myProfile || !this.state.peerProfile) return;
    const seed = generateSeed();
    this.state.phase = "battle";
    this.events.onBothReady(seed, this.state.config, this.state.myReadyTeam, this.state.myProfile, this.state.peerReadyTeam, this.state.peerProfile);
    this.updateState();
  }

  /** Submit an action for the current turn. */
  async submitAction(turn: number, action: BattleAction): Promise<void> {
    this.state.pendingAction = action;
    await this.conn.send({ type: "ACTION", turn, action });
    if (this.peerActionForTurn.has(turn)) {
      this.events.onTurnReady(turn, action, this.peerActionForTurn.get(turn)!);
      this.turnReadyCallback?.(turn, action, this.peerActionForTurn.get(turn)!);
    }
  }

  /** Set a callback to be invoked when both actions for a turn are ready. */
  setTurnReadyCallback(cb: (turn: number, myAction: BattleAction, peerAction: BattleAction) => void): void {
    this.turnReadyCallback = cb;
  }

  /** Set a callback to be invoked when a chat message is received. */
  setChatCallback(cb: (text: string) => void): void {
    this.chatCallback = cb;
  }

  /** Get the peer's action for a specific turn (null if not received yet). */
  getPeerAction(turn: number): BattleAction | null {
    return this.peerActionForTurn.get(turn) ?? null;
  }

  /** Check if both actions for a turn are ready. */
  isTurnReady(turn: number): boolean {
    return this.state.pendingAction !== null && this.peerActionForTurn.has(turn);
  }

  /** Advance to next turn after resolution. */
  advanceTurn(): void {
    this.state.turn++;
    this.state.pendingAction = null;
    this.updateState();
  }

  /** Mark battle as ready to start. */
  setReady(): void {
    this.state.phase = "ready";
    this.updateState();
  }

  /** Mark battle started. */
  setBattle(): void {
    this.state.phase = "battle";
    this.updateState();
  }

  /** Reset ready state when a peer disconnects before battle starts. */
  resetReady(): void {
    if (this.state.phase === "battle" || this.state.phase === "ended") return;
    this.state.myReady = false;
    this.state.peerReady = false;
    this.state.myReadyTeam = null;
    this.state.peerReadyTeam = null;
    this.state.phase = "ready";
    this.updateState();
  }

  /** Mark battle ended. */
  setEnded(): void {
    this.state.phase = "ended";
    this.updateState();
  }

  /** Broadcast the current battle state to any spectators. */
  async sendBattleState(state: BattleState, profiles: { p1: TrainerProfile; p2: TrainerProfile }): Promise<void> {
    await this.conn.broadcast({ type: "BATTLE_STATE", state, profiles });
  }

  /** Start heartbeat and listen for peer disconnect/reconnect from the connection. */
  startHeartbeat(): void {
    this.stopHeartbeat();
    this.state.peerConnected = true;
    this.state.lastPongTime = Date.now();
    this.heartbeatInterval = setInterval(() => {
      try {
        if (this.state.phase !== "ended" && this.state.phase !== "waiting") {
          void this.conn.send({ type: "PING", timestamp: Date.now() });
        }
      } catch (err) {
        console.warn("[SyncManager] heartbeat send failed:", err);
        this.markPeerDisconnected();
      }
    }, 5000);
    this.schedulePongCheck();
  }

  /** Stop heartbeat and clear timeouts. */
  stopHeartbeat(): void {
    if (this.heartbeatInterval) { clearInterval(this.heartbeatInterval); this.heartbeatInterval = null; }
    if (this.heartbeatTimeout) { clearTimeout(this.heartbeatTimeout); this.heartbeatTimeout = null; }
    if (this.reconnectTimeout) { clearTimeout(this.reconnectTimeout); this.reconnectTimeout = null; }
  }

  /** Mark peer disconnected and notify UI. */
  markPeerDisconnected(): void {
    if (!this.state.peerConnected) return;
    this.state.peerConnected = false;
    this.events.onPeerDisconnect();
    this.updateState();
    // Give up after a timeout
    this.reconnectTimeout = setTimeout(() => {
      this.events.onError("Opponent did not reconnect");
    }, 60000);
  }

  /** Mark peer reconnected and send current state so both sides can sync. */
  markPeerReconnected(): void {
    if (this.state.peerConnected || this.state.phase === "ended") return;
    this.state.peerConnected = true;
    this.state.lastPongTime = Date.now();
    if (this.reconnectTimeout) { clearTimeout(this.reconnectTimeout); this.reconnectTimeout = null; }
    void this.conn.send({
      type: "SYNC_STATE",
      turn: this.state.turn,
      config: this.state.config ?? { level: 50, turnTimerSeconds: 60, battleBackground: "grass" },
      pendingAction: this.state.pendingAction,
    });
    this.events.onPeerReconnect();
    this.updateState();
  }

  /** Request rematch. */
  async requestRematch(config: BattleConfig, seed: number): Promise<void> {
    await this.conn.send({ type: "REMATCH_REQUEST", config, seed });
  }

  /** Accept a rematch request. */
  async acceptRematch(config: BattleConfig, seed: number): Promise<void> {
    await this.conn.send({ type: "REMATCH_ACCEPT", config, seed });
  }

  /** Decline a rematch request. */
  async declineRematch(): Promise<void> {
    await this.conn.send({ type: "REMATCH_DECLINE" });
  }

  /** Set callbacks for rematch handshake. */
  setRematchCallbacks(
    onRequested: (config: BattleConfig, seed: number) => void,
    onAccepted: (config: BattleConfig, seed: number) => void,
    onDeclined: () => void,
  ): void {
    this.rematchRequestedCallback = onRequested;
    this.rematchAcceptedCallback = onAccepted;
    this.rematchDeclinedCallback = onDeclined;
  }

  /** Set a callback for peer disconnect. */
  setPeerDisconnectCallback(cb: () => void): void {
    // Replace the existing handler so the UI receives disconnect events
    const original = this.events.onPeerDisconnect;
    this.events.onPeerDisconnect = () => {
      original();
      cb();
    };
  }

  /** Set a callback for peer reconnect. */
  setPeerReconnectCallback(cb: () => void): void {
    const original = this.events.onPeerReconnect;
    this.events.onPeerReconnect = () => {
      original();
      cb();
    };
  }

  /** Set a callback for receiving spectator battle state updates. */
  setBattleStateCallback(cb: (state: BattleState, profiles: { p1: TrainerProfile; p2: TrainerProfile }) => void): void {
    this.events.onBattleState = cb;
  }

  /** Send chat message. */
  async sendChat(text: string): Promise<void> {
    await this.conn.send({ type: "CHAT", text });
  }

  private handlePong(): void {
    const wasConnected = this.state.peerConnected;
    this.state.peerConnected = true;
    this.state.lastPongTime = Date.now();
    if (!wasConnected && this.state.phase !== "ended") {
      if (this.reconnectTimeout) { clearTimeout(this.reconnectTimeout); this.reconnectTimeout = null; }
      this.events.onPeerReconnect();
    }
    this.schedulePongCheck();
    this.updateState();
  }

  private schedulePongCheck(): void {
    if (this.heartbeatTimeout) clearTimeout(this.heartbeatTimeout);
    this.heartbeatTimeout = setTimeout(() => {
      if (Date.now() - this.state.lastPongTime > 15000) {
        this.markPeerDisconnected();
      }
    }, 15000 + 500);
  }

  private applySyncState(turn: number, config: BattleConfig, pendingAction: BattleAction | null): void {
    this.state.turn = turn;
    this.state.config = config;
    if (pendingAction) {
      this.peerActionForTurn.set(turn, pendingAction);
    }
    this.events.onConfigReceived(config);
    this.updateState();
  }

  private updateState(): void {
    this.events.onStateChange({ ...this.state });
  }
}
